import type { SeoDataType } from "router/routes/betterAuthSearch";
import { lightTheme } from "styles/theme";
import { z } from "zod";

import { NumericRangeCriteria, SerialisedDateRangeCriteria } from "components/record_search/types";
import { SortState } from "lib/search/types";
import { VisitInfo } from "lib/UtmParams";
import { NoticesStagesv2, SubscriptionEnum } from "./graphQLEnums";

export type User = {
  guid: string;
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_verified: boolean;
  signup_utm?: VisitInfo;
  admin?: boolean;
  company_admin?: boolean;
  has_export_permission: boolean;
  has_account_briefing_permission: boolean;
  has_signal_settings: boolean;
  company: Company;
  data_type_subscriptions: DataTypeSubscription[];
  active_data_types: DataTypeName[];
  subscribed_data_types: DataTypeName[];
  created_at: string;
  subscription: SubscriptionEnum;
  team: {
    id: string;
    name: string;
  };
  seo_params?: {
    id?: string;
    dataType?: SeoDataType;
  } | null;
};

export type Company = {
  id: number;
  guid: string;
  name: string;
  self_serve: boolean;
  demo?: boolean;
  has_per_user_unread_state: boolean;
  hide_discard_button: boolean;
  spec_sheet_uid?: string | null;
  spec_sheet_url?: string | null;
  analytics_tags?: Record<string, string | boolean | number>;
  trial_start: string | null;
  trial_end: string | null;
  partner_programme: PartnerProgramme | null;
  has_international_data_access?: boolean;
  qualification_states: QualificationStates;
  payment_type: "Demo" | "Freemium" | "Paid" | null;
  created_at: string;
  allow_crm_integration?: boolean;
};

export type LeadAssigneeData = {
  guid: string;
  first_name: string;
  last_name: string;
  email: string;
};

export type RecordUserData = {
  lists?: UserListSummary[];
  seenByUsers: { first_name: string; guid: string }[];
};

export type DataTypeName =
  | "TENDERS|PRE-TENDERS"
  | "AWARDS"
  | "BUYERS|SUPPLIERS"
  | "NEWS"
  | "CONTACTS"
  | "FRAMEWORKS"
  | "DOCUMENTS"
  | "BIDS"
  | "MARKET_ANALYSIS";

// this is a slightly annoying exception to handle, where we treat BUYERS|SUPPLIERS as one
// data source (and want to keep it that way for some time to come), but it makes more sense
// from the user's point of view if they can see which *specific* source they need to get
// to access certain features. It would be misleading to say "you need to unlock buyers *and*
// suppliers" to access Supplier search, for instance
export type DataTypeNameWithBuyersAndSuppliers = DataTypeName | "BUYERS" | "SUPPLIERS";

export type ProDataTypeSeparateBuyersSuppliers = Exclude<
  DataTypeNameWithBuyersAndSuppliers,
  "TENDERS|PRE-TENDERS" | "BUYERS|SUPPLIERS"
>;

export type DataTypeSubscription = { data_type_name: DataTypeName; disabled: boolean };

export type BuyerCategoryAssignment = {
  buyer_category_id: string;
  source: string;
  parent_category_name?: string | null;
};

export type BuyerDetails = {
  id: number;
  name: string;
  country: string;
  url: string | null;
  postal_code: string | null;
  address: string | null;
  town: string | null;
  nuts_location: string | null;
  category: string | null;
  guid: string;
  categories: BuyerCategoryAssignment[];
  metadata: BuyerMetadata | null;
  is_archived: boolean;
  is_stotles_verified: boolean;
  enhanced_data: EnhancedBuyerData;
};

type EnhancedBuyerData = {
  oscar_category: string[] | null;
  employee_count: string | null;
  annual_spend: string | null;
  location_summary: string | null;
  url: string | null;
};

type BuyerMetadata = {
  logo_url: string | null;
  twitter_handle: string | null;
};

type CPVCode = {
  code: string;
  name: string;
  dimension_code: string;
};

export type SupplierSummary = {
  id: number;
  name: string;
  country: string | null;
  is_sme?: boolean | null;
  guid?: string;
  is_stotles_verified?: boolean;
};

export const CountryRegionSchema = z.object({
  countries: z.array(z.string()).optional(),
  regions: z.array(z.string()).optional(),
  selected: z.array(z.string()).optional(),
});
export type CountryRegion = z.infer<typeof CountryRegionSchema>;

export const AssigneeFilterSchema = z.object({
  ids: z.array(z.uuid()),
  includeNulls: z.boolean().optional(),
});
export type AssigneeFilter = z.infer<typeof AssigneeFilterSchema>;

/**
 * Drops empty user-id filters before sending to the GraphQL search request,
 * so the server doesn't see `{ ids: [], includeNulls: undefined }` as a real
 * filter. Used by both the assignee and createdBy fields on bid + lead search.
 */
export function nonEmptyUserFilter(filter: AssigneeFilter | undefined) {
  if (filter && (filter.ids.length > 0 || filter.includeNulls)) return filter;
  return undefined;
}

export const DateFilterSchema = z.object({
  filter: z
    .object({
      from: z.string().optional(),
      relativeFrom: z.string().optional(),
      to: z.string().optional(),
      relativeTo: z.string().optional(),
    })
    .optional(),
  hideNulls: z.boolean().optional(),
});
export type DateFilter = z.infer<typeof DateFilterSchema>;

export const ValueFilterSchema = z.object({
  from: z.number().optional(),
  to: z.number().optional(),
  hideNulls: z.boolean().optional(),
});
export type ValueFilter = z.infer<typeof ValueFilterSchema>;

type Lot = {
  id: number;
  lot_title: string | null;
  lot_number: string | null;
  award_date: string | null;
  value: number | null;
  currency: string | null;
  calculated_expiry: string | null;
  place_of_performance: string | null;
  end_date: string | null;
  start_date: string | null;
  suppliers: SupplierSummary[] | null;
};

export enum TenderStage {
  PRE_TENDER = "PRE_TENDER",
  STALE_PRE_TENDER = "STALE_PRE_TENDER",
  OPEN = "OPEN",
  CLOSED = "CLOSED",
  AWARDED = "AWARDED",
}

export enum TenderStageV2Groups {
  PRE_TENDER = "PRE_TENDER",
  TENDER = "TENDER",
  AWARD = "AWARD",
}
export type TenderStageV2GroupsKeys = keyof typeof TenderStageV2Groups;

type TenderStageInfo = {
  id: NoticesStagesv2;
  label: string;
  group: TenderStageV2Groups;
  color: string;
  tooltip: string;
};

export const tenderStages: Record<NoticesStagesv2, TenderStageInfo> = {
  [NoticesStagesv2.Pipeline]: {
    id: NoticesStagesv2.Pipeline,
    label: "Pipeline Notice",
    group: TenderStageV2Groups.PRE_TENDER,
    color: lightTheme.colors.sysProcStagePipeline,
    tooltip:
      "Commercial pipeline notices signal procurements expected to start in the next 18 months.",
  },
  [NoticesStagesv2.PreTenderV2]: {
    id: NoticesStagesv2.PreTenderV2,
    label: "Pre-tender",
    group: TenderStageV2Groups.PRE_TENDER,
    color: lightTheme.colors.sysProcStagePreTender,
    tooltip:
      'Advanced notice of an upcoming procurement. "Prior information notices (PINs)", "Preliminary market engagement notices", and equivalent notices are captured here.',
  },
  [NoticesStagesv2.StalePreTenderV2]: {
    id: NoticesStagesv2.StalePreTenderV2,
    label: "Stale Pre-tender",
    group: TenderStageV2Groups.PRE_TENDER,
    color: lightTheme.colors.sysProcStageStalePreTender,
    tooltip:
      "Pre-tenders older than 3 months, or where the engagement deadline has passed (e.g., a market engagement event).",
  },
  [NoticesStagesv2.CancelledPreTender]: {
    id: NoticesStagesv2.CancelledPreTender,
    label: "Cancelled pre-tender",
    group: TenderStageV2Groups.PRE_TENDER,
    color: lightTheme.colors.sysProcStageCancelledPreTender,
    tooltip:
      "A pre-tender notice whose procurement was cancelled before any tender or award notice was published.",
  },
  [NoticesStagesv2.OpenTender]: {
    id: NoticesStagesv2.OpenTender,
    label: "Open tender",
    group: TenderStageV2Groups.TENDER,
    color: lightTheme.colors.sysProcStageOpenTender,
    tooltip: "The buyer is currently accepting bid submissions.",
  },
  [NoticesStagesv2.ClosedTender]: {
    id: NoticesStagesv2.ClosedTender,
    label: "Closed tender",
    group: TenderStageV2Groups.TENDER,
    color: lightTheme.colors.sysProcStageClosedTender,
    tooltip: "The close date has passed, the buyer is no longer accepting submissions.",
  },
  [NoticesStagesv2.CancelledTender]: {
    id: NoticesStagesv2.CancelledTender,
    label: "Cancelled tender",
    group: TenderStageV2Groups.TENDER,
    color: lightTheme.colors.sysProcStageCancelledTender,
    tooltip: "The buyer has terminated the procurement process. No supplier will be awarded.",
  },
  [NoticesStagesv2.UnknownTender]: {
    id: NoticesStagesv2.UnknownTender,
    label: "Unknown tender",
    group: TenderStageV2Groups.TENDER,
    color: lightTheme.colors.sysProcStageUnknownTender,
    tooltip: "Insufficient data to define this tender's stage.",
  },
  [NoticesStagesv2.AwardedContract]: {
    id: NoticesStagesv2.AwardedContract,
    label: "Awarded contract",
    group: TenderStageV2Groups.AWARD,
    color: lightTheme.colors.sysProcStageAwardedContract,
    tooltip: `The contract has been awarded and is either about to start, or is currently in force.\n\nAwards where the contracting period are unknown are also included here.`,
  },
  [NoticesStagesv2.ExpiredContract]: {
    id: NoticesStagesv2.ExpiredContract,
    label: "Expired contract",
    group: TenderStageV2Groups.AWARD,
    color: lightTheme.colors.sysProcStageExpiredContract,
    tooltip: "The expiry date for this contract has passed, so it is no longer in force.",
  },
  [NoticesStagesv2.CancelledAward]: {
    id: NoticesStagesv2.CancelledAward,
    label: "Cancelled award",
    group: TenderStageV2Groups.AWARD,
    color: lightTheme.colors.sysProcStageCancelledAward,
    tooltip: "The award was announced, but it was cancelled before the contract started.",
  },
};

export function getRecordSignals(record: RecordDetails): LeadSignal[] | undefined {
  return record.signals;
}

export function getSignalTypes(signals?: BasicSignal[]) {
  return signals?.reduce<Record<string, string[]>>((types, nextSignal) => {
    if (types[nextSignal.category]) {
      types[nextSignal.category].push(nextSignal.name);
    } else {
      types[nextSignal.category] = [nextSignal.name];
    }
    return types;
  }, {});
}

export type RecordDetails = {
  type: "Tender" | "Contract" | "TenderIntent" | "BridgeNotice";
  guid: string;
  name: string;
  // TODO: Remove singular buyer once buyers fully implemented
  buyer: BuyerSummary;
  buyers: BuyerSummary[];
  url_path: string;
  canonical_url_path: string;
  country: string;

  value_src: number | null;
  currency: string | null;
  description?: string | null;
  url?: string;
  stage: TenderStage;

  close_date?: string | null;
  publish_date: string;
  award_date?: string | null;
  expiry_date?: string | null;
  tender_date?: string | null;

  tenders_received: number | null;
  lots?: Lot[];
  lists?: UserListSummary[];

  signals?: LeadSignal[];

  cpv_codes: CPVCode[];

  // TODO: remove these fields once RecordDetailsPage doesn't use them
  buyer_contact_email: string | null;
  buyer_contact_phone: string | null;
  buyer_contact_name: string | null;

  procurement_stage: ProcurementStage;
  hidden_date: string | null;
  assignee?: LeadAssigneeData;
  comment_count?: number;
  seen_by_users: { guid: string; first_name: string }[];
  added_at?: string | null;
  relevance_score?: number | null;
};

type ProcurementStage = {
  id: string;
  stage: string;
  record_family_id: string;
};

export type RecordPreview = {
  guid: string;
  name: string;
  description: string | null;
  publish_date: string;
  buyer: {
    id: number;
    guid: string;
    name: string;
  };
  stage: TenderStage;
};

export type BuyerSummary = {
  id: number;
  name: string;
  country: string;
  url?: string | null;
  postal_code?: string | null;
  address?: string | null;
  town?: string | null;
  nuts_location?: string | null;
  guid: string;
  oscar_id?: string | null;
  categories: BuyerCategoryAssignment[];
  is_stotles_verified: boolean;
  is_archived: boolean;
  metadata?: BuyerMetadata | null;
};

export type SignalCategory = "Keywords" | "CPV codes" | "Partners" | "Competitors";

export type LeadSignal = {
  id: string;
  name: string;
  category: SignalCategory;
  colour: string;
};

export type RecordListSummary = {
  id: number;
  name: string;
  company_id: number;
  owner_id: number | null;
  guid: string;
};

export type UserListSummary = RecordListSummary & {
  owner_id: number | null;
  guid: string;
};

export type OscarOrgDetails = {
  id: string;
  org_urn: string;
  archived_by_oscar: boolean;
  data: {
    Org: string;
    WebsiteUrl?: string;
    Add1?: string;
    Add2?: string;
    Add3?: string;
    Add4?: string;
    Town?: string;
    DataTypes?: string;
    OrgTypes?: string;
    // We can add fields as we start using it
    // but also have the flexibiltiy to look up any key
    [key: string]: any;
  };
};

export type PartnerProgramme = {
  name: string;
  id: string;
  referral_code: string;
  initial_data_types: DataTypeName[];
};

export const StatusValues = [
  "OPEN",
  "STALE_PRE_TENDER",
  "PRE_TENDER",
  "CLOSED",
  "AWARDED",
] as const;
export type Status = (typeof StatusValues)[number];

export type StageFilter =
  | "stale_pre_tender"
  | "pre_tender"
  | "open"
  | "closed"
  | "awarded"
  | "STALE_PRE_TENDER"
  | "PRE_TENDER"
  | "OPEN"
  | "CLOSED"
  | "AWARDED";

type NoticeColumn =
  | "date_added"
  | "title"
  | "indicator"
  | "stage"
  | "close_date"
  | "buyer"
  | "buyer_categories"
  | "user_tag"
  | "published_date"
  | "expiry_date"
  | "award_date"
  | "value"
  | "country"
  | "suppliers"
  | "is_sme"
  | "qualification"
  | "hidden_date"
  | "assignee";

export type BuyerColumn =
  | "buyer_name"
  | "matching_records"
  | "open_ops"
  | "expiries"
  | "competitors"
  | "partners"
  | "signals";

export type BuyerSearchColumn =
  | "buyer_name"
  | "notices"
  | "country"
  | "town"
  | "region"
  | "signals";

export type ColumnSetting<F> = {
  field: F;
  width?: number;
  title?: string;
  disabled?: boolean;
};

export type TableSettings<F> = {
  columns: ColumnSetting<F>[];
};

/**
 * This filter represents a sum of signals,
 * defined either by id or by category name.
 *
 * If a category name is specified, all the signals from that category will be used.
 */
export type SignalFilters = {
  categories: SignalCategory[] | undefined;
  ids: string[] | undefined;
};

type FilterSettings = {
  text?: string;
  stage?: StageFilter[];
  buyer_ids?: number[];
  buyer_guids?: string[];
  country_code?: string[];
  close_date?: SerialisedDateRangeCriteria;
  close_nulls?: boolean;
  added_date?: SerialisedDateRangeCriteria;
  publish_date?: SerialisedDateRangeCriteria;
  expiry_date?: SerialisedDateRangeCriteria;
  expiry_nulls?: boolean;
  signals?: SignalFilters;
  buyer_category_id?: string[];
  buyer_category_nulls?: boolean;
  buyer_regions?: string[];
  buyer_country?: string[];
  value?: NumericRangeCriteria;
  value_nulls?: boolean;
  signal_category?: string[]; // TODO: Figure out how this parameter interacts with `signals`
  show_user_hidden_leads?: UserHiddenLeadsFilter;
  include_all_disqualified_states?: boolean;
  qualification_states?: string[];
  assignee?: string[];
  buyer_list_id?: string; // TODO: Remove buyerListId after backfill of views to use buyer_list_ids instead of buyer_list_id
  buyer_list_ids?: string[];
};

export type SavedView = {
  id: string;
  team_id?: string | null;
  description?: string;
  owner_id: string | null;
  name: string;
  is_standard_view: boolean;
  data_type: string;
  view: {
    filterSettings: FilterSettings;
    sortOrder: SortState;
    tableSettings: TableSettings<NoticeColumn>;
  };
  updated_at?: string;
};

export type LeadSubscriptionFrequency = "DAILY" | "WEEKLY";

export type LeadSubscriptionType = "EMAIL" | "SLACK" | "TEAMS";

export type EmailSubscription = {
  id: string;
  user_id: string;
  company_id: string;
  frequency: LeadSubscriptionFrequency;
  type: "EMAIL";
  lead_subscription_contents: Record<string, LeadSubscriptionContents>;
  disabled: boolean;
};

export type IntegrationSubscription = {
  id: string;
  user_id: string;
  company_id: string;
  frequency: LeadSubscriptionFrequency;
  type: "SLACK" | "TEAMS";
  webhook_url: string;
  name: string;
  lead_subscription_contents: Record<string, LeadSubscriptionContents>;
  disabled: boolean;
};

export type LeadSubscription = EmailSubscription | IntegrationSubscription;

type LeadSubscriptionContents = {
  id: string;
  lead_subscription_id: string;
  resource_type: "SavedView" | "RecordList" | "SavedDocumentView";
  resource_id: string;
};

type QualificationStates = {
  default_qualification_state_id: string;
  default_disqualification_state_id: string;
  qualification_states: Record<string, QualificationStateConfig>;
} | null;

type QualificationStateConfig = {
  id: string;
  label: string;
  disqualifies_lead: boolean;
  company_id: string | null;
  rank: number;
};

export enum UserHiddenLeadsFilter {
  "SHOW_ALL" = "0",
  "SHOW_UNHIDDEN_ONLY" = "1",
  "SHOW_HIDDEN_ONLY" = "2",
}

export type BasicSignal = { id?: string; name: string; category: string };

// Used in filters as a "magic item" which selects all possible values of a field
export const SELECT_ALL = "__SELECT_ALL__";

export const ALL_COMPETITORS_TOKEN = "SIGNALS:ALL_COMPETITORS";
export const ALL_PARTNERS_TOKEN = "SIGNALS:ALL_PARTNERS";
export const ALL_KEYWORDS_TOKEN = "SIGNALS:ALL_KEYWORDS";
export const ALL_CPV_CODES_TOKEN = "SIGNALS:ALL_CPV_CODES";
